import { Plus, Trash, X, ChevronsUpDown } from 'lucide-react';
import { useRef, useCallback, useEffect, memo, useMemo } from 'react';
import { usePlaceholder } from '../../../hooks/usePlaceholder';
import { EmbalajesModal, getEmbalajes } from './EmbalajesModal';
import { OptimizedInput } from '../../common/OptimizedInput';
import { useDebouncedUpdate } from '../../../hooks/useDebouncedUpdate';
import { updateEmbalajeField, updateMarcaField } from '../../../utils/editableDataUpdates';
import { useIndexedFieldHandlerFactory } from '../../../utils/fieldHandlers';

const PackagesTableContentComponent = ({
  declarationIndex,
  itemIndex,
  isSpanish,
  item,
  editableData,
  setEditableData,
  setIsDirty,
  embalajesModalOpen,
  setEmbalajesModalOpen,
  currentEmbalajeIndex,
  setCurrentEmbalajeIndex,
}) => {
  const embalajes = getEmbalajes();

  const editableDataRef = useRef(editableData);

  useEffect(() => {
    editableDataRef.current = editableData;
  }, [editableData]);

  // debounced update callback
  const handleUpdate = useCallback((updatedData) => {
    setEditableData(updatedData);
    setIsDirty(true);
  }, [setEditableData, setIsDirty]);

  const { debouncedUpdate } = useDebouncedUpdate(handleUpdate, 300);

  const handleEmbalajeChange = useCallback((embajaIndex, field, value) => {
    const currentEditableData = editableDataRef.current;
    const updatedData = updateEmbalajeField(
      currentEditableData,
      declarationIndex,
      itemIndex,
      embajaIndex,
      field,
      value
    );
    
    // only proceed if data actually changed
    if (updatedData === currentEditableData) {
      return;
    }

    editableDataRef.current = updatedData;
    
    // OPTIMIZED: Immediately validate field to remove error if field is now filled
    if (typeof window !== 'undefined' && window.__validateFieldImmediately) {
      window.__validateFieldImmediately(declarationIndex, field, value, itemIndex, null, embajaIndex);
    }
    
    debouncedUpdate(updatedData);
  }, [declarationIndex, itemIndex, debouncedUpdate]);

  const handleMarcaChange = useCallback((embajaIndex, marcaIndex, value) => {
    const currentEditableData = editableDataRef.current;
    const updatedData = updateMarcaField(
      currentEditableData,
      declarationIndex,
      itemIndex,
      embajaIndex,
      marcaIndex,
      value
    );
    
    // only proceed if data actually changed
    if (updatedData === currentEditableData) {
      return;
    }

    editableDataRef.current = updatedData;
    
    // OPTIMIZED: Immediately validate marca field to remove error if field is now filled
    if (typeof window !== 'undefined' && window.__validateFieldImmediately) {
      // Marca fields use special field name format and need marcaIndex
      window.__validateFieldImmediately(declarationIndex, 'marcas_marca', value, itemIndex, null, embajaIndex, marcaIndex);
    }
    
    debouncedUpdate(updatedData);
  }, [declarationIndex, itemIndex, debouncedUpdate]);

  const handleEmbalajeSelect = useCallback((selectedEmbalaje) => {
    if (currentEmbalajeIndex !== null) {
      handleEmbalajeChange(currentEmbalajeIndex, 'tipo', selectedEmbalaje.codigo);
    }
    setEmbalajesModalOpen(false);
    setCurrentEmbalajeIndex(null);
  }, [currentEmbalajeIndex, handleEmbalajeChange]);

  const getSelectedEmbalaje = (tipo) => {
    return embalajes.find(embalaje => embalaje.codigo === tipo);
  };

  // handler for embalaje fields 
  const embalajeFieldHandlers = useRef({});
  const getEmbalajeFieldHandler = useCallback((embajaIndex, field) => {
    const key = `${embajaIndex}_${field}`;
    if (!embalajeFieldHandlers.current[key]) {
      embalajeFieldHandlers.current[key] = (e) => {
        let value = e.target.value;
        // For cantidad_restante, ensure it's an integer or null/empty string
        if (field === 'cantidad_restante') {
          const trimmedValue = value.trim();
          if (trimmedValue === '' || trimmedValue === null || trimmedValue === undefined) {
            value = '';
          } else {
            const parsed = parseInt(trimmedValue, 10);
            value = isNaN(parsed) ? '' : parsed;
          }
        }
        handleEmbalajeChange(embajaIndex, field, value);
      };
    }
    return embalajeFieldHandlers.current[key];
  }, [handleEmbalajeChange]);
  
  // handler for marca fields
  const marcaFieldHandlers = useRef({});
  const getMarcaFieldHandler = useCallback((embajaIndex, marcaIndex) => {
    const key = `${embajaIndex}_${marcaIndex}`;
    if (!marcaFieldHandlers.current[key]) {
      marcaFieldHandlers.current[key] = (e) => handleMarcaChange(embajaIndex, marcaIndex, e.target.value);
    }
    return marcaFieldHandlers.current[key];
  }, [handleMarcaChange]);

  // Memoize all placeholder values to prevent unnecessary re-renders
  const enterQuantityPlaceholder = usePlaceholder({ isSpanish, type: 'enter_quantity' });
  const enterRemainingQuantityPlaceholder = usePlaceholder({ isSpanish, type: 'enter_remaining_quantity' });
  const enterPiecesPlaceholder = usePlaceholder({ isSpanish, type: 'enter_pieces' });
  const enterInternalExternalPlaceholder = usePlaceholder({ isSpanish, type: 'enter_internal_external' });
  const enterMarkPlaceholder = usePlaceholder({ isSpanish, type: 'enter_mark' });

  return (
    <div className="overflow-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr>
          <th className="py-2 px-4 pt-2 pl-0">
              <p className="w-max">#</p>
            </th>
            <th className="py-2 px-4 pt-2"><p className='w-max'>{!isSpanish ? "Type" : "Tipo"}</p></th>
            <th className="py-2 px-4 pt-2"><p className='w-max'>{!isSpanish ? "Quantity" : "Cantidad"}</p></th>
            <th className="py-2 px-4 pt-2"><p className='w-max'>{!isSpanish ? "Remaining Quantity" : "Cantidad Restante"}</p></th>
            <th className="py-2 px-4 pt-2"><p className='w-max'>{!isSpanish ? "Pieces" : "Piezas"}</p></th>
            <th className="py-2 px-4 pt-2"><p className='w-max'>{!isSpanish ? "Internal or External" : "Es Interno o Externo"}</p></th>
            <th className="py-2 px-4 pt-2"><p className='w-max'>{!isSpanish ? "Marks" : "Marcas"}</p></th>
            <th className="py-2 px-4 pt-2"></th>
          </tr>
        </thead>
        <tbody>
          {item.embalajes?.map((embalaje, embajaIndex) => (
            <tr key={embajaIndex}>
              <td className="py-4 px-4 border-t pl-0">
                <span className="text-sm">{embajaIndex + 1}</span>
              </td>
              <td className="py-4 px-4 border-t">
                <div className="relative w-full">
                  <button
                    type="button"
                    id={`packages_tipo_${declarationIndex + 1}_${itemIndex}_${embajaIndex}`}
                    className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                      getSelectedEmbalaje(embalaje.tipo) ? "" : "opacity-75"
                    }`}
                    onClick={() => {
                      setCurrentEmbalajeIndex(embajaIndex);
                      setEmbalajesModalOpen(true);
                    }}
                  >
                    {getSelectedEmbalaje(embalaje.tipo) ? (
                      <span className="truncate">
                        {getSelectedEmbalaje(embalaje.tipo).codigo}{" "}
                        {getSelectedEmbalaje(embalaje.tipo).nombre && (
                          <span className="text-xs text-gray-400 ml-2">
                            ({getSelectedEmbalaje(embalaje.tipo).nombre})
                          </span>
                        )}
                      </span>
                    ) : embalaje.tipo ? (
                      <span className="truncate" title={embalaje.tipo}>
                        {embalaje.tipo}
                      </span>
                    ) : (
                      <span className="truncate">
                        {!isSpanish
                          ? "Select Package Type"
                          : "Seleccionar Tipo de Embalaje"}
                      </span>
                    )}
                    <ChevronsUpDown className="w-4 h-4" />
                  </button>
                </div>
              </td>
              <td className="py-4 px-4 border-t">
                <OptimizedInput
                  id={`packages_cantidad_${declarationIndex + 1}_${itemIndex}_${embajaIndex}`}
                  value={embalaje.cantidad || ''}
                  placeholder={enterQuantityPlaceholder}
                  onChange={getEmbalajeFieldHandler(embajaIndex, 'cantidad')}
                  className="w-full border rounded px-2 py-1"
                />
              </td>
              <td className="py-4 px-4 border-t">
                <OptimizedInput
                  id={`packages_cantidad_restante_${declarationIndex + 1}_${itemIndex}_${embajaIndex}`}
                  value={embalaje?.cantidad_restante || ''}
                  type='number'
                  placeholder={enterRemainingQuantityPlaceholder}
                  onChange={getEmbalajeFieldHandler(embajaIndex, 'cantidad_restante')}
                  className="w-full border rounded px-2 py-1"
                />
              </td>
              <td className="py-4 px-4 border-t">
                <OptimizedInput
                  id={`packages_piezas_${declarationIndex + 1}_${itemIndex}_${embajaIndex}`}
                  value={embalaje?.piezas || ''}
                  placeholder={enterPiecesPlaceholder}
                  onChange={getEmbalajeFieldHandler(embajaIndex, 'piezas')}
                  className="w-full border rounded px-2 py-1"
                />
              </td>
              <td className="py-4 px-4 border-t">
                <OptimizedInput
                  value={embalaje.es_interno_o_externo || ''}
                  placeholder={enterInternalExternalPlaceholder}
                  onChange={getEmbalajeFieldHandler(embajaIndex, 'es_interno_o_externo')}
                  className={`w-full border rounded px-2 py-1 ${!embalaje.es_interno_o_externo && 'required-input'}`}
                  id={`packages_es_interno_o_externo_${declarationIndex + 1}_${itemIndex}_${embajaIndex}`}
                  required
                />
              </td>
              <td className="py-4 px-4 border-t" id={`packages_marcas_${declarationIndex + 1}_${itemIndex}_${embajaIndex}`}>
                <div className='w-fit flex flex-col gap-1'>
                  {embalaje.marcas?.map((marca, marcaIndex) => (
                    <div key={marcaIndex} className="flex items-center gap-2">
                      <OptimizedInput
                        value={marca.marca || ''}
                        placeholder={enterMarkPlaceholder}
                        onChange={getMarcaFieldHandler(embajaIndex, marcaIndex)}
                        className={`min-w-20 border rounded px-2 py-1 ${!marca.marca && 'required-input'}`}
                        id={`packages_marcas_marca_${declarationIndex + 1}_${itemIndex}_${embajaIndex}_${marcaIndex}`}
                        required
                      />
                      <button
                        className="text-red-500"
                        onClick={() => {
                          const updatedData = [...editableData];
                          updatedData[declarationIndex].partidas[itemIndex].embalajes[embajaIndex].marcas.splice(marcaIndex, 1);
                          setEditableData(updatedData);
                          setIsDirty(true);
                        }}
                      >
                        <X className="size-4 stroke-red-500 mt-1" />
                      </button>
                    </div>
                  ))}
                  <button
                    className="text-xs flex items-center gap-1 mt-1 w-max"
                    onClick={() => {
                      const updatedData = [...editableData];
                      if (!updatedData[declarationIndex].partidas[itemIndex].embalajes[embajaIndex].marcas) {
                        updatedData[declarationIndex].partidas[itemIndex].embalajes[embajaIndex].marcas = [];
                      }
                      updatedData[declarationIndex].partidas[itemIndex].embalajes[embajaIndex].marcas.push({
                        marca: ""
                      });
                      setEditableData(updatedData);
                      setIsDirty(true);
                    }}
                  >
                    <Plus className="size-4" />
                    {isSpanish ? "Agregar Marca" : "Add Mark"}
                  </button>
                </div>
              </td>
              <td className="py-4 px-4 border-t">
                <button
                  className="py-1 text-xs flex items-center gap-1 text-red-500"
                  onClick={() => {
                    const updatedData = [...editableData];
                    updatedData[declarationIndex].partidas[itemIndex].embalajes.splice(embajaIndex, 1);
                    setEditableData(updatedData);
                    setIsDirty(true);
                  }}
                >
                  <Trash className={`size-4 stroke-red-500`} />
                  {isSpanish ? "Eliminar" : "Delete"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      <EmbalajesModal
        embalajes={embalajes}
        isOpen={embalajesModalOpen}
        onClose={() => {
          setEmbalajesModalOpen(false);
          setCurrentEmbalajeIndex(null);
        }}
        onSelect={handleEmbalajeSelect}
        selectedEmbalaje={currentEmbalajeIndex !== null ? getSelectedEmbalaje(item.embalajes?.[currentEmbalajeIndex]?.tipo) : null}
        title={!isSpanish ? "Select Package Type" : "Seleccionar Tipo de Embalaje"}
      />
    </div>
  );
};

// memoize component to prevent re-renders when props haven't changed
const arePackagesTableContentPropsEqual = (prevProps, nextProps) => {
  if (prevProps.declarationIndex !== nextProps.declarationIndex) return false;
  if (prevProps.itemIndex !== nextProps.itemIndex) return false;
  if (prevProps.isSpanish !== nextProps.isSpanish) return false;
  if (prevProps.setEditableData !== nextProps.setEditableData) return false;
  if (prevProps.setIsDirty !== nextProps.setIsDirty) return false;
  if (prevProps.embalajesModalOpen !== nextProps.embalajesModalOpen) return false;
  if (prevProps.currentEmbalajeIndex !== nextProps.currentEmbalajeIndex) return false;

  const prevItem = prevProps.item;
  const nextItem = nextProps.item;

  if (prevItem === nextItem) return true;

  if ((prevItem?.embalajes?.length || 0) !== (nextItem?.embalajes?.length || 0)) return false;
  
  return true;
};

export const PackagesTableContent = memo(PackagesTableContentComponent, arePackagesTableContentPropsEqual);

